export const SITE_URL: string =
  (import.meta.env.VITE_SITE_URL as string | undefined) || "https://cssbuybr.guru";

export const SITE_NAME = "CSSBuyBR Guru";

export const LOGO_URL =
  "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png";

export interface SeoData {
  title: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  ogSiteName?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
}

function upsertMeta(attr: "name" | "property", key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function applySeoMeta(data: SeoData): void {
  document.title = data.title;
  if (data.description) upsertMeta("name", "description", data.description);
  if (data.keywords) upsertMeta("name", "keywords", data.keywords);
  if (data.canonical) upsertCanonical(data.canonical);

  if (data.ogTitle) upsertMeta("property", "og:title", data.ogTitle);
  if (data.ogDescription) upsertMeta("property", "og:description", data.ogDescription);
  if (data.ogType) upsertMeta("property", "og:type", data.ogType);
  if (data.ogUrl) upsertMeta("property", "og:url", data.ogUrl);
  if (data.ogImage) upsertMeta("property", "og:image", data.ogImage);
  if (data.ogSiteName) upsertMeta("property", "og:site_name", data.ogSiteName);

  if (data.twitterCard) upsertMeta("name", "twitter:card", data.twitterCard);
  if (data.twitterTitle) upsertMeta("name", "twitter:title", data.twitterTitle);
  if (data.twitterDescription) upsertMeta("name", "twitter:description", data.twitterDescription);
  if (data.twitterImage) upsertMeta("name", "twitter:image", data.twitterImage);
}

export function setJsonLd(schema: object | object[], id = "page-jsonld"): void {
  document.head
    .querySelectorAll(`script[data-seo-jsonld="${id}"]`)
    .forEach((node) => node.remove());

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-seo-jsonld", id);
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
}

export function clearJsonLd(id = "page-jsonld"): void {
  document.head
    .querySelectorAll(`script[data-seo-jsonld="${id}"]`)
    .forEach((node) => node.remove());
}

export function languageCode(lang: string): string {
  if (lang === "pt") return "pt-BR";
  if (lang === "de") return "de";
  return "en";
}

export interface ArticleJsonLdInput {
  headline: string;
  description: string;
  image?: string;
  canonical: string;
  inLanguage: string;
  faq?: { question: string; answer: string }[];
}

export function buildArticleJsonLd(input: ArticleJsonLdInput): object {
  const article: Record<string, unknown> = {
    "@type": "Article",
    headline: input.headline,
    description: input.description,
    inLanguage: input.inLanguage,
    datePublished: "2026-08-30",
    dateModified: "2026-08-30",
    author: { "@type": "Organization", name: SITE_NAME },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: LOGO_URL },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": input.canonical },
  };

  if (input.image) {
    article.image = input.image;
  }

  const graph: object[] = [article];

  if (input.faq && input.faq.length > 0) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: input.faq.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

export interface WebsiteJsonLdInput {
  description: string;
  canonical: string;
  inLanguage: string;
}

export function buildWebsiteJsonLd(input: WebsiteJsonLdInput): object {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        name: SITE_NAME,
        url: input.canonical,
        inLanguage: input.inLanguage,
        description: input.description,
      },
      {
        "@type": "Organization",
        name: SITE_NAME,
        url: input.canonical,
        logo: { "@type": "ImageObject", url: LOGO_URL },
      },
    ],
  };
}